import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import PersonalizationSection from "@/components/account/PersonalizationSection";
import AccountActivitySection from "@/components/account/AccountActivitySection";
import {
    updateAccountProfile,
    updateNotificationPrefs,
    requestAccountDeletion,
    getMyDeletionRequest,
    listDashboardReports,
    myEntitlements,
    openBillingPortal,
    fetchBillingSubscription,
    deleteReport,
    exportMyData,
} from "@/lib/api";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { ArrowUpRight, Shield, Trash2, Bell, CreditCard, FileText, UserCircle, Mail, Download, Lock, CheckCircle2 } from "lucide-react";
import QuotesSection from "@/components/account/QuotesSection";
import DeleteReportDialog from "@/components/dashboard/DeleteReportDialog";
import { track } from "@/lib/analytics";
import { sendVerificationEmail, changeAccountPassword, changeAccountEmail } from "@/lib/api";

function Section({ title, description, eyebrow, children, testid }) {
    return (
        <section
            className="bl-card p-6 md:p-8 space-y-5"
            data-testid={testid}
            id={testid}
        >
            {eyebrow && <div className="bl-eyebrow">{eyebrow}</div>}
            <div>
                <h2 className="font-display font-semibold text-2xl text-bl tracking-tight">
                    {title}
                </h2>
                {description && (
                    <p className="mt-2 text-sm text-bl-muted max-w-2xl leading-relaxed">
                        {description}
                    </p>
                )}
            </div>
            {children}
        </section>
    );
}

function ProfileSection({ user, onSaved }) {
    const [name, setName] = useState(user.name || "");
    const [saving, setSaving] = useState(false);
    const dirty = (user.name || "") !== (name || "");

    const save = async () => {
        try {
            setSaving(true);
            const fresh = await updateAccountProfile({ name });
            toast.success("Profile updated");
            onSaved?.(fresh);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update profile.");
        } finally {
            setSaving(false);
        }
    };

    const providerLabel =
        user.auth_provider === "google" ? "Google" : "Email & password";
    const createdLabel = user.created_at
        ? new Date(user.created_at).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
          })
        : "—";

    return (
        <Section
            eyebrow="Profile"
            title="Your account"
            testid="account-profile-section"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Name
                    </div>
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        maxLength={80}
                        className="mt-2 w-full px-3 py-2.5 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                        data-testid="account-name-input"
                        placeholder="Your name"
                    />
                </label>
                <div className="block">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Email
                    </div>
                    <div className="mt-2 w-full px-3 py-2.5 bg-bl-bg/40 rounded-lg border border-bl text-bl-muted text-sm font-mono-bl">
                        {user.email}
                    </div>
                    <p className="mt-2 text-xs text-bl-muted leading-relaxed">
                        To change your account email, scroll to the{" "}
                        <a href="#account-email-login-section" className="text-bl-primary hover:underline">
                            Email &amp; login
                        </a>{" "}
                        section below — you&apos;ll re-enter your password and verify the new address.
                    </p>
                </div>
                <div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Sign-in method
                    </div>
                    <div className="mt-2 text-sm text-bl">{providerLabel}</div>
                </div>
                <div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Member since
                    </div>
                    <div className="mt-2 text-sm text-bl">{createdLabel}</div>
                </div>
            </div>
            <div>
                <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    className="bl-btn-primary"
                    data-testid="account-save-profile-btn"
                >
                    {saving ? "Saving…" : "Save changes"}
                </button>
            </div>
        </Section>
    );
}

function EmailLoginSection({ user, onChanged }) {
    const [sending, setSending] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [changeOpen, setChangeOpen] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [emailPw, setEmailPw] = useState("");
    const [changeError, setChangeError] = useState(null);
    const [submittingChange, setSubmittingChange] = useState(false);
    const [pendingEmail, setPendingEmail] = useState(null);
    const verified = !!user.email_verified;
    const isOauth = user.auth_provider !== "email";

    const resend = async () => {
        if (sending || cooldown > 0) return;
        setSending(true);
        try {
            await sendVerificationEmail();
            toast.success("Verification email sent.");
            let s = 60;
            setCooldown(s);
            const id = setInterval(() => {
                s -= 1;
                setCooldown(s);
                if (s <= 0) clearInterval(id);
            }, 1000);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not send verification email.",
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <Section
            eyebrow="Email & login"
            title="How you sign in"
            description="Your email address, verification status, and sign-in method."
            testid="account-email-login-section"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Email
                    </div>
                    <div className="mt-2 w-full px-3 py-2.5 bg-bl-bg/40 rounded-lg border border-bl text-bl text-sm font-mono-bl">
                        {user.email}
                    </div>
                </div>
                <div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Verification
                    </div>
                    <div
                        className="mt-2 inline-flex items-center gap-2 text-sm"
                        data-testid={`account-verification-status-${verified ? "verified" : "pending"}`}
                    >
                        {verified ? (
                            <>
                                <CheckCircle2 className="w-4 h-4 text-bl-primary" />
                                <span className="text-bl">Verified</span>
                            </>
                        ) : isOauth ? (
                            <>
                                <CheckCircle2 className="w-4 h-4 text-bl-primary" />
                                <span className="text-bl">
                                    Verified via Google
                                </span>
                            </>
                        ) : (
                            <>
                                <Mail className="w-4 h-4 text-amber-600" />
                                <span className="text-amber-800">
                                    Pending — check your inbox
                                </span>
                            </>
                        )}
                    </div>
                    {!verified && !isOauth && (
                        <button
                            type="button"
                            onClick={resend}
                            disabled={sending || cooldown > 0}
                            className="mt-3 text-sm text-bl-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="account-resend-verification-btn"
                        >
                            {cooldown > 0
                                ? `Resend in ${cooldown}s`
                                : sending
                                  ? "Sending…"
                                  : "Resend verification email"}
                        </button>
                    )}
                </div>
                <div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Sign-in method
                    </div>
                    <div className="mt-2 text-sm text-bl">
                        {isOauth ? "Google" : "Email & password"}
                    </div>
                </div>
            </div>
            {!isOauth && (
                <div className="pt-3 border-t border-bl">
                    {pendingEmail ? (
                        <div
                            className="text-sm text-bl-muted bg-bl-bg/40 border border-bl rounded-md p-3"
                            data-testid="account-email-change-pending"
                        >
                            We sent a verification link to{" "}
                            <span className="text-bl font-medium">
                                {pendingEmail}
                            </span>
                            . Your account email will update once you click
                            the link. Until then, sign in with{" "}
                            <span className="text-bl">{user.email}</span>.
                        </div>
                    ) : !changeOpen ? (
                        <button
                            type="button"
                            onClick={() => setChangeOpen(true)}
                            className="text-sm text-bl-primary hover:underline"
                            data-testid="account-change-email-open-btn"
                        >
                            Change email address →
                        </button>
                    ) : (
                        <form
                            onSubmit={async (e) => {
                                e.preventDefault();
                                setChangeError(null);
                                if (!newEmail.trim() || !emailPw) return;
                                setSubmittingChange(true);
                                try {
                                    const r = await changeAccountEmail(
                                        newEmail.trim().toLowerCase(),
                                        emailPw,
                                    );
                                    setPendingEmail(r.pending_email || newEmail);
                                    try { track("change_email_requested"); } catch { /* non-critical */ }
                                    toast.success(
                                        "Verification email sent to your new address.",
                                    );
                                    setChangeOpen(false);
                                    setNewEmail("");
                                    setEmailPw("");
                                } catch (err) {
                                    setChangeError(
                                        err?.response?.data?.detail ||
                                            "Could not change email.",
                                    );
                                } finally {
                                    setSubmittingChange(false);
                                }
                            }}
                            className="space-y-3 max-w-md"
                            data-testid="account-change-email-form"
                        >
                            <div className="text-sm text-bl-muted">
                                We'll send a verification link to the new
                                address. Your account email switches once you
                                verify — the current email keeps working
                                until then.
                            </div>
                            <label className="block">
                                <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    New email
                                </div>
                                <input
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={newEmail}
                                    onChange={(e) =>
                                        setNewEmail(e.target.value)
                                    }
                                    className="mt-2 w-full px-3 py-2.5 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                    data-testid="account-new-email-input"
                                />
                            </label>
                            <label className="block">
                                <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Confirm with your current password
                                </div>
                                <input
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    value={emailPw}
                                    onChange={(e) =>
                                        setEmailPw(e.target.value)
                                    }
                                    className="mt-2 w-full px-3 py-2.5 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                                    data-testid="account-email-confirm-password-input"
                                />
                            </label>
                            {changeError && (
                                <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
                                    {changeError}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    disabled={submittingChange}
                                    className="bl-btn-primary"
                                    data-testid="account-change-email-submit-btn"
                                >
                                    {submittingChange
                                        ? "Sending…"
                                        : "Send verification"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setChangeOpen(false);
                                        setChangeError(null);
                                        setNewEmail("");
                                        setEmailPw("");
                                    }}
                                    className="bl-btn-secondary"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}
        </Section>
    );
}

function PasswordSecuritySection({ user }) {
    const isOauth = user.auth_provider !== "email";
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (next.length < 8) {
            setError("New password must be at least 8 characters.");
            return;
        }
        if (next !== confirm) {
            setError("New passwords do not match.");
            return;
        }
        if (next === current) {
            setError("New password must be different from your current one.");
            return;
        }
        setSaving(true);
        try {
            await changeAccountPassword(current, next);
            toast.success("Password updated.");
            setCurrent("");
            setNext("");
            setConfirm("");
        } catch (e) {
            const detail = e?.response?.data?.detail || "Could not update password.";
            setError(detail);
        } finally {
            setSaving(false);
        }
    };

    if (isOauth) {
        return (
            <Section
                eyebrow="Password & security"
                title="Managed by Google"
                description="You signed in with Google. Password changes are managed through your Google account."
                testid="account-password-section-oauth"
            >
                <div className="text-sm text-bl-muted">
                    <Lock className="inline w-4 h-4 mr-2 text-bl-primary" />
                    To change your password, visit your Google account
                    security settings.
                </div>
            </Section>
        );
    }

    return (
        <Section
            eyebrow="Password & security"
            title="Change your password"
            description="Updating your password will sign you out of all other devices, but not this one."
            testid="account-password-section"
        >
            <form onSubmit={submit} className="space-y-4 max-w-md">
                <label className="block">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Current password
                    </div>
                    <input
                        type="password"
                        autoComplete="current-password"
                        value={current}
                        onChange={(e) => setCurrent(e.target.value)}
                        required
                        className="mt-2 w-full px-3 py-2.5 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                        data-testid="account-current-password-input"
                    />
                </label>
                <label className="block">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        New password
                    </div>
                    <input
                        type="password"
                        autoComplete="new-password"
                        value={next}
                        onChange={(e) => setNext(e.target.value)}
                        required
                        minLength={8}
                        className="mt-2 w-full px-3 py-2.5 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                        data-testid="account-new-password-input"
                    />
                </label>
                <label className="block">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Confirm new password
                    </div>
                    <input
                        type="password"
                        autoComplete="new-password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        required
                        minLength={8}
                        className="mt-2 w-full px-3 py-2.5 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                        data-testid="account-confirm-password-input"
                    />
                </label>
                {error && (
                    <div
                        className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3"
                        data-testid="account-password-error"
                    >
                        {error}
                    </div>
                )}
                <button
                    type="submit"
                    disabled={saving || !current || !next || !confirm}
                    className="bl-btn-primary"
                    data-testid="account-change-password-btn"
                >
                    {saving ? "Updating…" : "Update password"}
                </button>
            </form>
        </Section>
    );
}

function NotificationsSection({ user, onSaved }) {
    const [optIn, setOptIn] = useState(!!user.email_opt_in);
    const [digestOptIn, setDigestOptIn] = useState(user.weekly_digest_opt_in !== false);
    const [saving, setSaving] = useState(false);
    const isInvestor = !!user?.tier?.tier;

    const toggleMarketing = async (next) => {
        try {
            setOptIn(next);
            setSaving(true);
            const fresh = await updateNotificationPrefs({ email_opt_in: next });
            onSaved?.(fresh);
            toast.success(
                next
                    ? "Subscribed to buyer insights."
                    : "Unsubscribed from buyer insights.",
            );
        } catch (e) {
            setOptIn(!next);
            toast.error(
                e?.response?.data?.detail || "Could not update preferences.",
            );
        } finally {
            setSaving(false);
        }
    };

    const toggleDigest = async (next) => {
        try {
            setDigestOptIn(next);
            setSaving(true);
            const fresh = await updateNotificationPrefs({ weekly_digest_opt_in: next });
            onSaved?.(fresh);
            toast.success(
                next
                    ? "Weekly investor digest enabled."
                    : "Weekly investor digest paused.",
            );
        } catch (e) {
            setDigestOptIn(!next);
            toast.error(
                e?.response?.data?.detail || "Could not update preferences.",
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Section
            eyebrow="Email"
            title="Notification preferences"
            description="Required emails — payment receipts, report-ready notices, account security — are always sent. The opt-ins below cover optional content."
            testid="account-notifications-section"
        >
            <div className="space-y-3">
                <div className="flex items-start gap-4 bl-card p-5">
                    <div className="mt-0.5">
                        <Bell className="w-5 h-5 text-bl-primary" />
                    </div>
                    <div className="flex-1">
                        <div className="font-display text-bl">
                            Buyer insights and homeowner guidance
                        </div>
                        <p className="text-sm text-bl-muted mt-1.5 leading-relaxed">
                            Get strategic buyer insights, inspection tips, and
                            homeowner guidance delivered occasionally.
                        </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={optIn}
                            onChange={(e) => toggleMarketing(e.target.checked)}
                            disabled={saving}
                            data-testid="account-email-optin-toggle"
                        />
                        <div className="w-11 h-6 bg-bl-bg border border-bl rounded-full peer peer-checked:bg-bl-primary peer-checked:border-bl-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-bl-surface after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                </div>
                {isInvestor && (
                    <div
                        className="flex items-start gap-4 bl-card p-5"
                        data-testid="account-weekly-digest-card"
                    >
                        <div className="mt-0.5">
                            <Bell className="w-5 h-5 text-bl-primary" />
                        </div>
                        <div className="flex-1">
                            <div className="font-display text-bl">
                                Weekly investor digest
                            </div>
                            <p className="text-sm text-bl-muted mt-1.5 leading-relaxed">
                                A calm Monday snapshot of your pipeline: property
                                count, pipeline status, total repair exposure,
                                and your top properties by risk. Only sent when
                                you have at least one tracked property.
                            </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={digestOptIn}
                                onChange={(e) => toggleDigest(e.target.checked)}
                                disabled={saving}
                                data-testid="account-weekly-digest-toggle"
                            />
                            <div className="w-11 h-6 bg-bl-bg border border-bl rounded-full peer peer-checked:bg-bl-primary peer-checked:border-bl-primary transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-bl-surface after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                        </label>
                    </div>
                )}
            </div>
        </Section>
    );
}

function ReportsSection({ onChanged }) {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const r = await listDashboardReports();
            setReports(r);
        } catch {
            setReports([]);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    return (
        <Section
            eyebrow="Reports + data"
            title="Your saved reports"
            description="Inspection reports can contain sensitive property and personal information. Buyer's Leverage gives you control over your saved reports and account data."
            testid="account-reports-section"
        >
            {loading ? (
                <div className="text-bl-muted text-sm">Loading…</div>
            ) : reports.length === 0 ? (
                <div className="text-bl-muted text-sm">
                    You don't have any saved reports yet.{" "}
                    <Link to="/upload" className="text-bl-primary hover:underline">
                        Upload an inspection
                    </Link>
                    .
                </div>
            ) : (
                <ul className="space-y-2.5" data-testid="account-reports-list">
                    {reports.map((r) => (
                        <li
                            key={r.report_id}
                            className="bl-card p-4 flex items-center justify-between gap-3"
                            data-testid={`account-report-row-${r.report_id}`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                                    <FileText className="w-3 h-3" />
                                    {r.owned_tier === "closing_strategy"
                                        ? "Closing Strategy"
                                        : r.owned_tier === "summary"
                                          ? "Inspection Summary"
                                          : "Preview"}
                                    {r.status === "generating" && (
                                        <span className="bl-badge bl-badge-medium ml-1">
                                            Generating…
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1 font-display text-bl truncate">
                                    {r.property_address || "Untitled report"}
                                </div>
                                <div className="mt-0.5 text-xs text-bl-muted font-mono-bl">
                                    Uploaded{" "}
                                    {new Date(r.created_at).toLocaleDateString()}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <Link
                                    to={`/report/${r.report_id}`}
                                    className="bl-btn-ghost text-sm"
                                    data-testid={`account-report-view-btn-${r.report_id}`}
                                >
                                    View
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => setDeleteTarget(r)}
                                    className="bl-btn-ghost text-sm hover:text-rust"
                                    style={{ color: "#C85B3A" }}
                                    data-testid={`account-report-delete-btn-${r.report_id}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            <DeleteReportDialog
                report={deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onDeleted={() => {
                    setDeleteTarget(null);
                    load();
                    onChanged?.();
                }}
            />
        </Section>
    );
}

function BillingSection() {
    const [ent, setEnt] = useState({ entitlements: [], subscriptions: [] });
    const [sub, setSub] = useState(null);
    const [opening, setOpening] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const e = await myEntitlements();
                setEnt(e);
            } catch { /* non-critical */ }
            try {
                const s = await fetchBillingSubscription();
                setSub(s);
            } catch { /* non-critical */ }
        })();
    }, []);

    const openPortal = async () => {
        try {
            setOpening(true);
            const r = await openBillingPortal();
            if (r?.supported && r?.url) {
                window.open(r.url, "_blank", "noopener");
            } else {
                toast.info(
                    r?.message ||
                        "Billing portal support is coming soon. Contact support for help with billing.",
                );
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not open billing portal.",
            );
        } finally {
            setOpening(false);
        }
    };

    const fmtMoney = (n, currency = "usd") =>
        n == null
            ? "—"
            : new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: (currency || "usd").toUpperCase(),
                  maximumFractionDigits: 0,
              }).format(Number(n));

    const fmtDate = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        } catch {
            return iso;
        }
    };

    return (
        <Section
            eyebrow="Billing"
            title="Purchases & subscriptions"
            testid="account-billing-section"
        >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                    className="bl-card p-5"
                    data-testid="billing-subscription-card"
                >
                    <div className="flex items-start justify-between gap-2">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            Active subscription
                        </div>
                        {sub && (
                            <span
                                className={
                                    "bl-badge text-[10px] " +
                                    (sub.status === "active"
                                        ? "bl-badge-low"
                                        : sub.status === "past_due"
                                            ? "bl-badge-high"
                                            : "bl-badge-neutral")
                                }
                                data-testid="billing-sub-status"
                            >
                                {sub.status}
                            </span>
                        )}
                    </div>
                    {sub ? (
                        <>
                            <div
                                className="mt-2 font-display text-2xl text-bl"
                                data-testid="billing-sub-plan"
                            >
                                {sub.plan_label}
                            </div>
                            <div className="mt-1 text-bl-muted text-sm">
                                <span className="font-mono-bl text-bl">
                                    {fmtMoney(sub.amount, sub.currency)}
                                </span>
                                {" / "}
                                {sub.interval || "month"}
                            </div>
                            <div className="mt-4 text-xs text-bl-muted leading-relaxed">
                                {sub.cancel_at_period_end ? (
                                    <>
                                        Cancels on{" "}
                                        <span className="text-bl font-mono-bl">
                                            {fmtDate(sub.current_period_end)}
                                        </span>
                                        . Resume anytime from Manage Billing.
                                    </>
                                ) : (
                                    <>
                                        Renews on{" "}
                                        <span className="text-bl font-mono-bl">
                                            {fmtDate(sub.current_period_end)}
                                        </span>
                                    </>
                                )}
                            </div>
                            {sub.pricing_locked && sub.current_period_end && (
                                <div
                                    className="mt-3 inline-flex items-start gap-2 px-3 py-2 rounded-md bg-[var(--bl-green-soft)]/40 border border-[var(--bl-green)]/30 text-xs leading-relaxed"
                                    data-testid="pricing-lock-assurance"
                                >
                                    <Lock className="h-3.5 w-3.5 text-[var(--bl-green)] mt-0.5 shrink-0" />
                                    <span className="text-bl">
                                        <span className="font-medium text-[var(--bl-green)]">Pricing locked.</span>{" "}
                                        Current rate guaranteed through{" "}
                                        <span className="font-mono-bl">
                                            {fmtDate(sub.current_period_end)}
                                        </span>
                                        {" "}— future price increases won't affect your subscription.
                                    </span>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="mt-2 font-display text-bl">None</div>
                    )}
                </div>
                <div className="bl-card p-5">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                        One-time purchases
                    </div>
                    <div
                        className="mt-2 font-display text-2xl text-bl tnum"
                        data-testid="billing-purchases-count"
                    >
                        {ent.entitlements?.length || 0}
                    </div>
                    <div className="mt-1 text-bl-muted text-sm">
                        report unlocks across your account
                    </div>
                </div>
            </div>
            <div>
                <button
                    type="button"
                    onClick={openPortal}
                    disabled={opening}
                    className="bl-btn-primary"
                    data-testid="account-billing-portal-btn"
                >
                    <CreditCard className="w-4 h-4" />
                    {opening ? "Opening…" : "Manage Billing"}
                </button>
            </div>
        </Section>
    );
}

function AccountDeletionSection({ user, onRefresh }) {
    const [open, setOpen] = useState(false);
    const [confirmation, setConfirmation] = useState("");
    const [busy, setBusy] = useState(false);
    const [existing, setExisting] = useState(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await getMyDeletionRequest();
                setExisting(r);
            } catch { /* non-critical */ }
        })();
    }, []);

    const submit = async () => {
        try {
            setBusy(true);
            await requestAccountDeletion(confirmation);
            toast.success("Account deletion request submitted.");
            setOpen(false);
            setConfirmation("");
            const r = await getMyDeletionRequest();
            setExisting(r);
            onRefresh?.();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Could not submit deletion request.",
            );
        } finally {
            setBusy(false);
        }
    };

    const downloadData = async () => {
        try {
            setExporting(true);
            const data = await exportMyData();
            const blob = new Blob([JSON.stringify(data, null, 2)], {
                type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const stamp = new Date().toISOString().slice(0, 10);
            a.download = `buyers-leverage-data-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Your data download is ready.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not export data.");
        } finally {
            setExporting(false);
        }
    };

    return (
        <Section
            eyebrow="Privacy + account deletion"
            title="Your data"
            description="Inspection reports can contain sensitive property and personal information. Deleting your account may remove access to saved reports and analysis. Some transaction records may be retained where legally required for tax, fraud prevention, or support purposes."
            testid="account-deletion-section"
        >
            <div className="bl-card p-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div>
                    <div className="font-display text-bl flex items-center gap-2">
                        <Download className="w-4 h-4 text-bl-primary" />
                        Download my data
                    </div>
                    <p className="mt-1.5 text-sm text-bl-muted leading-relaxed max-w-xl">
                        Export your profile, reports, uploads, purchase
                        history, and audit trail as a JSON file. Useful before
                        requesting deletion.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={downloadData}
                    disabled={exporting}
                    className="bl-btn-secondary shrink-0"
                    data-testid="account-export-btn"
                >
                    <Download className="w-4 h-4" />
                    {exporting ? "Preparing…" : "Download JSON"}
                </button>
            </div>

            {existing ? (
                <div className="bl-card p-5" data-testid="account-deletion-pending">
                    <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-bl-primary mt-0.5" />
                        <div>
                            <div className="font-display text-bl">
                                Deletion request received
                            </div>
                            <p className="mt-1.5 text-sm text-bl-muted leading-relaxed">
                                We've received your request to delete your
                                account on{" "}
                                <span className="text-bl">
                                    {new Date(
                                        existing.requested_at,
                                    ).toLocaleDateString()}
                                </span>
                                . Our team is reviewing it and will complete the
                                removal soon. Status:{" "}
                                <span className="text-bl">{existing.status}</span>
                                .
                            </p>
                        </div>
                    </div>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="bl-btn-danger"
                    data-testid="account-request-deletion-btn"
                >
                    <Trash2 className="w-4 h-4" />
                    Request Account Deletion
                </button>
            )}

            <Dialog open={open} onOpenChange={(v) => !v && !busy && setOpen(false)}>
                <DialogContent
                    className="max-w-md bg-bl-surface border-bl rounded-2xl"
                    data-testid="account-deletion-dialog"
                >
                    <DialogHeader>
                        <div className="bl-eyebrow w-fit">
                            Privacy + account deletion
                        </div>
                        <DialogTitle className="font-display text-2xl text-bl mt-2">
                            Request account deletion?
                        </DialogTitle>
                        <DialogDescription className="text-bl-muted leading-relaxed">
                            We'll begin removing your account data, uploaded
                            reports, and saved analysis. Some billing or
                            transaction records may be retained where required
                            for legal, tax, fraud prevention, or support
                            purposes.
                        </DialogDescription>
                    </DialogHeader>
                    <label className="block mt-2">
                        <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                            Type DELETE to confirm
                        </div>
                        <input
                            value={confirmation}
                            onChange={(e) => setConfirmation(e.target.value)}
                            placeholder="DELETE"
                            className="mt-2 w-full px-3 py-2.5 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary font-mono-bl"
                            data-testid="account-deletion-confirm-input"
                        />
                    </label>
                    <DialogFooter className="mt-4 flex flex-row gap-3 justify-end">
                        <button
                            type="button"
                            onClick={() => {
                                setConfirmation("");
                                setOpen(false);
                            }}
                            disabled={busy}
                            className="bl-btn-secondary"
                            data-testid="account-deletion-cancel-btn"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={busy || confirmation.trim().toUpperCase() !== "DELETE"}
                            className="bl-btn-danger"
                            data-testid="account-deletion-confirm-btn"
                        >
                            {busy ? "Submitting…" : "Request deletion"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Section>
    );
}

export default function AccountSettings() {
    const { user, loading, refresh } = useAuth();

    if (loading) {
        return (
            <PublicLayout>
                <div className="bl-container py-20 text-center text-bl-muted">
                    Loading…
                </div>
            </PublicLayout>
        );
    }
    if (!user) {
        return <Navigate to="/auth/login?next=/account" replace />;
    }

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container pt-14 pb-8" data-testid="account-settings-page">
                    <div className="bl-eyebrow flex items-center gap-2">
                        <UserCircle className="w-3.5 h-3.5" /> Account
                    </div>
                    <h1 className="mt-4 font-display font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tight text-bl leading-tight">
                        Settings
                    </h1>
                    <p className="mt-4 text-bl-muted max-w-2xl leading-relaxed">
                        Manage your profile, notifications, saved reports,
                        billing, and privacy in one place.
                    </p>
                    <div className="mt-5">
                        <Link
                            to="/dashboard"
                            className="inline-flex items-center gap-1.5 text-sm text-bl-muted hover:text-bl-primary transition-colors"
                            data-testid="account-back-to-dashboard"
                        >
                            ← Back to dashboard
                        </Link>
                    </div>
                </div>
            </section>

            <section className="bl-container pb-20 space-y-6">
                <ProfileSection user={user} onSaved={() => refresh?.()} />
                <PersonalizationSection user={user} onSaved={() => refresh?.()} />
                <EmailLoginSection user={user} onChanged={() => refresh?.()} />
                <PasswordSecuritySection user={user} />
                <AccountActivitySection />
                <NotificationsSection user={user} onSaved={() => refresh?.()} />
                <ReportsSection />
                <QuotesSection />
                <BillingSection />
                <AccountDeletionSection user={user} onRefresh={() => refresh?.()} />
            </section>
        </PublicLayout>
    );
}
